/**
 * useFlowMapData
 *
 * Fetches link geometry for the flow animation (TripsLayer) via the minify
 * endpoint (/links/locations/{id}/minify), which returns an array of GeoJSON
 * FeatureCollections with GEOM1 diameter data used to size the trip trails.
 *
 * Returns only the parsed GisLine array — nodes are not needed for animation.
 *
 * Re-fetches automatically when locationId changes.
 * Resets to an empty array when locationId is null.
 */

import { useState, useEffect, useMemo } from 'react';
import { getLinksMinify } from '@/services/links';
import { parseGisResponse, EMPTY_GIS_DATA } from '@/utils/gisUtils';
import type { GisLine } from '@/utils/gisUtils';

export type { GisLine } from '@/utils/gisUtils';

export function useFlowMapData(locationId: number | null | undefined): GisLine[] {
	const [rawResponse, setRawResponse] = useState<any>(null);
	const [currentLocationId, setCurrentLocationId] = useState<number | null | undefined>(locationId);

	useEffect(() => {
		if (!locationId) {
			setRawResponse(null);
			setCurrentLocationId(null);
			return;
		}

		if (locationId === currentLocationId && rawResponse !== null) return;

		setCurrentLocationId(locationId);
		setRawResponse(null);

		getLinksMinify(locationId)
			.then((response: any) => {
				setRawResponse(response ?? []);
			})
			.catch((err: any) => {
				console.error('[useFlowMapData] Failed to load flow data:', err);
				setRawResponse([]);
			});
	}, [locationId]);

	return useMemo<GisLine[]>(() => {
		if (!locationId || rawResponse === null) return EMPTY_GIS_DATA.lines;
		return parseGisResponse(rawResponse).lines;
	}, [locationId, rawResponse]);
}

export default useFlowMapData;
